import { useState, useEffect } from "react";
import { Box, Typography, Link, CircularProgress } from "@mui/material";
import { events as FALLBACK_EVENTS } from "../data/data.js";

const API_BASE = "https://www.africabuildtoshelter.com/api";

const LABEL_COLORS = ["#5C3311", "#E5793B", "#4E9E8C", "#DDA63A"];

function getImageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_BASE.replace("/api", "")}${path.startsWith("/") ? "" : "/"}${path}`;
}

function useApi(endpoint) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}${endpoint}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [endpoint]);

  return { data, loading, error };
}

export default function EventsSection() {
  const [filter, setFilter] = useState("all");
  const { data, loading, error } = useApi(`/events/?filter=${filter}`);

  // Use API data if available, otherwise fallback to hardcoded
  const apiEvents = data?.events ?? null;
  const events    = apiEvents ?? FALLBACK_EVENTS.filter((e) =>
    filter === "all" ? true : (filter === "past" ? true : false)
  );

  const total    = data?.total    ?? FALLBACK_EVENTS.length;
  const past     = data?.past     ?? FALLBACK_EVENTS.length;
  const upcoming = data?.upcoming ?? 0;

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        py: { xs: 10, md: 14 },
        px: { xs: 3, md: 6, lg: 10 },
        color: "#000000",
      }}
    >
      {/* ── Heading + filters ── */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        mb: { xs: 6, md: 8, lg: 10 },
      }}>
        <Typography
          sx={{
            fontSize: { xs: "2.8rem", sm: "3.8rem", md: "5rem", lg: "6rem" },
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
          }}
        >
          EVENTS
        </Typography>

        {/* Filter buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {[
            { key: "all",      label: `All (${total})` },
            { key: "past",     label: `Past (${past})` },
            { key: "upcoming", label: `Upcoming (${upcoming})` },
          ].map((f) => (
            <Box
              component="button"
              key={f.key}
              onClick={() => setFilter(f.key)}
              sx={{
                px: 2, py: 0.7, borderRadius: "3px",
                fontSize: "12px", fontWeight: 700,
                letterSpacing: "0.5px", cursor: "pointer",
                transition: "all 0.2s", border: "1.5px solid",
                borderColor: filter === f.key ? "#5C3311" : "rgba(26,10,0,0.2)",
                bgcolor: filter === f.key ? "#5C3311" : "transparent",
                color: filter === f.key ? "#fff" : "rgba(26,10,0,0.5)",
                "&:hover": { borderColor: "#5C3311", color: filter === f.key ? "#fff" : "#5C3311" },
              }}
            >
              {f.label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Content ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#5C3311" }} />
        </Box>
      ) : events.length === 0 ? (
        <Typography sx={{ color: "rgba(26,10,0,0.4)", textAlign: "center", py: 4 }}>
          No events found.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: { xs: 4, md: 6, lg: 8 },
            overflowX: { xs: "auto", md: "visible" },
            pb: { xs: 4, md: 0 },
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#cccccc", borderRadius: 3 },
          }}
        >
          {events.map((event, idx) => {
            // Normalise field names between API and hardcoded shapes
            const isApi     = !!apiEvents;
            const imgSrc    = isApi ? getImageUrl(event.image) : `/${event.image}`;
            const title     = event.title;
            const desc      = isApi ? event.description : event.desc;
            const dateLabel = isApi ? event.date_label  : `${event.day ?? ""} ${event.month ?? ""}`.trim();
            const location  = isApi ? event.location    : event.loc;
            const isPast    = isApi ? event.is_past     : true;
            const label     = isApi
              ? (event.event_type_display ?? event.event_type ?? "EVENT")
              : (event.type ?? "EVENT");
            const labelColor = LABEL_COLORS[idx % LABEL_COLORS.length];

            return (
              <Box
                key={event.id ?? event.title}
                sx={{
                  flex: "0 0 33.333%",
                  minWidth: { xs: "80%", md: "33.333%" },
                  maxWidth: { md: "380px", lg: "420px" },
                }}
              >
                {/* Image */}
                <Box sx={{ mb: { xs: 3, md: 4 }, position: "relative" }}>
                  {imgSrc ? (
                    <Box
                      component="img"
                      src={imgSrc}
                      alt={title}
                      loading="lazy"
                      sx={{
                        width: "100%",
                        height: { xs: 220, sm: 260, md: 300, lg: 340 },
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box sx={{
                      width: "100%",
                      height: { xs: 220, sm: 260, md: 300, lg: 340 },
                      bgcolor: "#e8ddd1",
                    }} />
                  )}
                  {/* Past / Upcoming badge */}
                  <Box sx={{
                    position: "absolute", top: 10, left: 10,
                    bgcolor: isPast ? "rgba(92,51,17,0.85)" : "#DDA63A",
                    color: "#fff", fontSize: "9px", fontWeight: 800,
                    letterSpacing: "1.5px", textTransform: "uppercase",
                    px: 1.2, py: 0.5, borderRadius: "2px",
                  }}>
                    {isPast ? "Past" : "● Upcoming"}
                  </Box>
                </Box>

                {/* Label + date */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: labelColor }}>
                    {label}
                  </Typography>
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "rgba(26,10,0,0.25)" }} />
                  <Typography sx={{ fontSize: "10px", color: "rgba(26,10,0,0.45)", fontWeight: 500 }}>
                    {dateLabel}
                  </Typography>
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    fontSize: { xs: "1.35rem", md: "1.5rem", lg: "1.65rem" },
                    fontWeight: 700,
                    lineHeight: 1.3,
                    mb: 1.5,
                  }}
                >
                  {title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: "0.98rem",
                    lineHeight: 1.6,
                    color: "#444444",
                    mb: 3,
                  }}
                >
                  {desc}
                </Typography>

                {/* TIME block */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "#000000",
                      mb: 0.75,
                    }}
                  >
                    TIME
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.96rem",
                      lineHeight: 1.65,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {dateLabel || "Date TBA"}
                    {"\n"}
                    {location || "Location TBA"}
                  </Typography>
                </Box>

                {/* View Location */}
               
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}